import React, {
  CSSProperties,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  darken,
  Divider,
  Paper,
  SvgIconProps,
  SxProps,
  useTheme,
} from "@mui/material";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelGroupStorage,
} from "react-resizable-panels";
import { merge } from "lodash";
import { usePaperElevation } from "shared-ts-ui-elevation";
import { SwipeableSupportingPaneHeader } from "./SwipeableSupportingPaneHeader";
import { Variant } from "../Variant";
import { MainPaneProps } from "../shared/MainPaneProps";
import { SupportingPaneProvider } from "../shared/SupportingPaneProvider";
import { SupportingPaneProps } from "../shared/SupportingPaneProps";
import { PageHeader } from "../shared/PageHeader";
import { SupportingPane } from "../shared/SupportingPane";
import { ResizeHandle } from "../shared/ResizeHandle";
import { useResolveResizePanelProps } from "../shared/useResolveResizePanelProps";
import { useSupportingPanel } from "../shared/useSupportingPanel";
import { usePaneSizes } from "../usePaneSizes";
import {
  PaneDimensionsProvider,
  PaneSizeProvider,
} from "shared-ts-ui-responsive";

export interface AppPageSwipeableBottomPaneProps {
  $key: string;
  name: string;
  width?: number;
  height?: number;
  variant?: Variant;
  hideName?: boolean;
  icon?: React.ComponentType<SvgIconProps>;
  mainPaneProps?: MainPaneProps;
  mainPaneActions?: ReactNode;
  children?: ReactNode;
  supportingPane?: ReactElement | SupportingPaneProvider;
  supportingPaneProps?: SupportingPaneProps;
  disableResize?: boolean;
  onPanelGroupStorage: PanelGroupStorage;
}

export const AppPageSwipeableBottomPane: FC<AppPageSwipeableBottomPaneProps> = (
  props
) => {
  const { mainPaneProps, supportingPaneProps } = props;
  const theme = useTheme();
  const variant = props.variant ?? "elevation";
  const disableResize = props.disableResize ?? false;
  const paperVariant = variant === "flat" ? "outlined" : variant;
  const paneSizes = usePaneSizes();
  const defaultPaperElevation = usePaperElevation();
  const [supportingPaneToolbarContainer, setSupportingPaneToolbarContainer] =
    useState<Element | null>(null);
  const displayHeader = props.mainPaneActions != null || !props.hideName;
  const toolbarContainerRef = React.useRef<Element | null>(null);
  const supportingPanelRef = useRef<ImperativePanelHandle>(null);

  useEffect(() => {
    if (toolbarContainerRef.current != null) {
      setSupportingPaneToolbarContainer(toolbarContainerRef.current);
    }
  }, [toolbarContainerRef]);

  const resizePanelProps = useResolveResizePanelProps(
    "mobile",
    supportingPaneProps?.resizePanelProps
  );
  const supportingPanelResizePanelStyle: CSSProperties = useMemo(() => {
    const overflowY = supportingPaneProps?.scrollableContentVertically
      ? "auto"
      : undefined;
    const webkitOverflowScrolling =
      supportingPaneProps?.scrollableContentVertically ? "touch" : undefined;

    return merge({}, resizePanelProps?.style, {
      overflowY: overflowY,
      WebkitOverflowScrolling: webkitOverflowScrolling,
      maxHeight: "unset",
      minHeight: "unset",
      paddingTop: theme.spacing(0.1),
      paddingRight: theme.spacing(variant === "flat" ? 0 : 1),
      paddingLeft: theme.spacing(variant === "flat" ? 0 : 1),
      paddingBottom: theme.spacing(variant === "flat" ? 0 : 1),
      display: "flex",
      flexDirection: "column",
    });
  }, [
    supportingPaneProps?.scrollableContentVertically,
    resizePanelProps?.style,
    theme,
    variant,
  ]);
  const supportingPaneSx: SxProps = useMemo(
    () =>
      merge(supportingPaneProps?.sx, {
        flexGrow: 1,
        overflowY: "auto",
        p: 1,
      }),
    [supportingPaneProps?.sx]
  );

  const supportingPanelProps = useSupportingPanel({
    $key: props.$key,
    supportPanelRef: supportingPanelRef,
    direction: "vertical",
    resizePanelProps: resizePanelProps,
    width: props.width,
    height: props.height,
  });

  const handleSupportingPaneToolbarContainerMount = (element: Element) => {
    setSupportingPaneToolbarContainer(element);
  };

  return (
    <PanelGroup
      direction={"vertical"}
      autoSaveId={"AppPage"}
      storage={props.onPanelGroupStorage}
    >
      <Panel
        id={`${props.$key}-main`}
        style={{
          display: "flex",
          flexDirection: "column",
          paddingTop: variant === "flat" ? theme.spacing(0) : theme.spacing(1),
          paddingRight:
            variant === "flat" ? theme.spacing(0) : theme.spacing(1),
          paddingLeft: variant === "flat" ? theme.spacing(0) : theme.spacing(1),
          paddingBottom:
            variant === "flat"
              ? theme.spacing(0)
              : variant === "elevation"
              ? theme.spacing(0.1)
              : 0,
        }}
      >
        <Paper
          ref={paneSizes.mainPane.ref}
          variant={paperVariant}
          className={
            "MainPaneContainer MainPaneContainer-swipeable-bottom-pane"
          }
          elevation={
            paperVariant === "elevation"
              ? mainPaneProps?.paperElevation != null
                ? mainPaneProps?.paperElevation
                : defaultPaperElevation
              : undefined
          }
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            ...(variant === "flat" && {
              border: "unset",
              backgroundColor: "unset",
              boxShadow: "unset",
              ">:not(.MuiDivider-root):not(.PageHeader)": {
                px: mainPaneProps?.disableGutters === true ? undefined : 1,
                pt: mainPaneProps?.disableGutters === true ? undefined : 1,
              },
            }),
            ...(variant === "elevation" && {
              ">:not(.MuiDivider-root):not(.PageHeader)": {
                p: mainPaneProps?.disableGutters === true ? undefined : 1,
              },
            }),
            ...(variant === "outlined" && {
              ">:not(.MuiDivider-root):not(.PageHeader)": {
                p: mainPaneProps?.disableGutters === true ? undefined : 1,
              },
            }),
            ...(mainPaneProps?.scrollableContentVertically === true && {
              paddingBottom: 0,
              ">:not(.PageHeader)": {
                overflowY: "auto",
                touchAction: "pan-y", // Enables vertical touch scrolling
                WebkitOverflowScrolling: "touch", // Give scroll momentum on iOS devices
              },
            }),
          }}
        >
          <PaneDimensionsProvider
            width={paneSizes.mainPane.width}
            height={paneSizes.mainPane.height}
          >
            <PaneSizeProvider
              width={paneSizes.mainPane.width}
              height={paneSizes.mainPane.height}
            >
              {displayHeader && (
                <>
                  <PageHeader
                    variant={variant}
                    title={props.name}
                    icon={props.icon}
                    hideTitle={props.hideName}
                  >
                    {props.mainPaneActions}
                  </PageHeader>
                  <Divider />
                </>
              )}
              {paneSizes.mainPane.width !== undefined &&
                paneSizes.mainPane.height !== undefined &&
                props.children}
            </PaneSizeProvider>
          </PaneDimensionsProvider>
        </Paper>
      </Panel>
      {props.supportingPane != null && (
        <>
          <ResizeHandle
            type={"mobile"}
            orientation={"horizontal"}
            variant={variant}
            label={supportingPaneProps?.label}
            collapsed={!supportingPanelProps.supportingPaneOpen}
            hideLabel={false}
            hideHandle={false}
            disabled={disableResize}
            style={{
              marginTop: 0,
              marginBottom: 0,
              marginLeft: "3px",
              marginRight: "3px",
              backgroundColor: darken(theme.palette.background.paper, 0.01),
            }}
            onOpen={supportingPanelProps.resizeHandleOnOpen}
          />
          <Panel
            id={`${props.$key}-support`}
            ref={supportingPanelRef}
            order={2}
            collapsible={true}
            collapsedSize={0}
            defaultSize={supportingPanelProps.supportingPanelDefaultSize}
            minSize={supportingPanelProps.supportingPanelMinSize}
            maxSize={supportingPanelProps.supportingPanelMaxSize}
            onCollapse={supportingPanelProps.supportingPanelOnCollapse}
            onExpand={supportingPanelProps.supportingPanelOnExpand}
            style={supportingPanelResizePanelStyle}
          >
            {props.supportingPane && supportingPaneProps && (
              <Box
                className={"SupportingPaneContainer"}
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "3px",
                  marginRight: "3px",
                  borderLeft: "1px solid " + theme.palette.divider,
                  borderRight: "1px solid " + theme.palette.divider,
                  paddingTop: theme.spacing(0),
                  paddingLeft: theme.spacing(0),
                  paddingRight: theme.spacing(0),
                  paddingBottom: theme.spacing(0),
                  ...(variant === "flat" && {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: 0,
                    backgroundColor: darken(
                      theme.palette.background.paper,
                      0.01
                    ),
                    boxShadow: "unset",
                  }),
                }}
              >
                <SwipeableSupportingPaneHeader
                  label={supportingPaneProps?.label ?? ""}
                  hideLabel={true}
                  disableResize={disableResize}
                  onToolbarContainerMount={
                    handleSupportingPaneToolbarContainerMount
                  }
                  onChange={supportingPanelProps.supportingPanelOnToggleOpen}
                />
                <SupportingPane
                  ref={paneSizes.supportingPane.ref}
                  sx={supportingPaneSx}
                  open={supportingPanelProps.supportingPaneOpen}
                  onToggleOpen={
                    supportingPanelProps.supportingPanelOnToggleOpen
                  }
                >
                  {paneSizes.supportingPane.width !== undefined &&
                    paneSizes.supportingPane.height !== undefined && (
                      <PaneDimensionsProvider
                        width={paneSizes.supportingPane.width}
                        height={paneSizes.supportingPane.height}
                      >
                        <PaneSizeProvider
                          width={paneSizes.supportingPane.width}
                          height={paneSizes.supportingPane.height}
                        >
                          {typeof props.supportingPane === "function"
                            ? supportingPaneToolbarContainer != null
                              ? props.supportingPane({
                                  toolbarContainer:
                                    supportingPaneToolbarContainer,
                                  layout: "SwipeableBottomPane",
                                })
                              : null
                            : props.supportingPane}
                        </PaneSizeProvider>
                      </PaneDimensionsProvider>
                    )}
                </SupportingPane>
              </Box>
            )}
          </Panel>
        </>
      )}
    </PanelGroup>
  );
};
